import { isError, isObject, isString } from 'lodash'
import { parseScript, type ESTree } from 'meriyah'
import type { ComponentDefinition, CustomElementEntry } from '../types'
import { DEFINITION_GETTER_NAME } from './constants'
import { astToValue } from './jsLiteralValue'

const isCustomElementClass = (node: ESTree.ClassDeclaration): boolean => {
    if (!node.superClass) {
        return false
    }
    if (node.superClass.type === 'Identifier' && node.superClass.name === 'HTMLElement') {
        return true
    }
    return false
}

const findDefinitionGetter = (classBody: ESTree.ClassBody): ESTree.MethodDefinition | undefined => {
    for (const element of classBody.body) {
        if (
            element.type === 'MethodDefinition' &&
            element.kind === 'get' &&
            element.static &&
            element.key &&
            element.key.type === 'Identifier' &&
            element.key.name === DEFINITION_GETTER_NAME
        ) {
            return element
        }
    }
    return undefined
}

const extractDefinitionFromGetter = (getter: ESTree.MethodDefinition, className: string): ComponentDefinition => {
    const functionBody = getter.value
    if (functionBody.type !== 'FunctionExpression' || !functionBody.body) {
        throw new Error(`Class "${className}": definition getter must be a function`)
    }

    for (const stmt of functionBody.body.body) {
        if (stmt.type === 'ReturnStatement' && stmt.argument) {
            const value = astToValue(stmt.argument)

            if (!isObject(value) || Array.isArray(value)) {
                throw new Error(`Class "${className}": definition must return an object`)
            }

            if (!('componentType' in value)) {
                throw new Error(`Class "${className}": definition must have componentType property`)
            }

            if (!isString(value.componentType)) {
                throw new Error(`Class "${className}": componentType must be a string`)
            }

            return value as ComponentDefinition
        }
    }
    throw new Error(`Class "${className}": definition getter must have a return statement`)
}

const buildDefineCallMap = (ast: ESTree.Program): Map<string, string> => {
    const map = new Map<string, string>()

    for (const node of ast.body) {
        if (node.type !== 'ExpressionStatement') {
            continue
        }
        const expr = node.expression
        if (expr.type !== 'CallExpression') {
            continue
        }
        if (expr.callee.type !== 'MemberExpression') {
            continue
        }
        const { object, property } = expr.callee
        if (
            object.type !== 'Identifier' ||
            object.name !== 'customElements' ||
            property.type !== 'Identifier' ||
            property.name !== 'define'
        ) {
            continue
        }
        const [tagArg, classArg] = expr.arguments
        if (!tagArg || !classArg) {
            continue
        }
        if (tagArg.type !== 'Literal' || !isString(tagArg.value)) {
            continue
        }
        if (classArg.type !== 'Identifier') {
            continue
        }
        map.set(classArg.name, tagArg.value)
    }

    return map
}

export const parseCustomElementsModule = (jsContent: string): CustomElementEntry[] => {
    const entries: CustomElementEntry[] = []

    if (!jsContent.trim()) {
        return entries
    }

    let ast: ESTree.Program
    try {
        ast = parseScript(jsContent, { next: true })
    } catch (error) {
        const message = isError(error) ? error.message : String(error)
        throw new Error(`Failed to parse elements.js: ${message}`)
    }

    const defineCallMap = buildDefineCallMap(ast)

    for (const node of ast.body) {
        if (node.type !== 'ClassDeclaration' || !isCustomElementClass(node)) {
            continue
        }
        const className = node.id?.name
        if (!className) {
            continue
        }

        const tag = defineCallMap.get(className)
        if (!tag) {
            continue
        }

        const getter = findDefinitionGetter(node.body)
        if (!getter) {
            continue
        }

        const definition = extractDefinitionFromGetter(getter, className)

        entries.push({
            tag,
            definition,
        })
    }

    return entries
}
